/**
 * PathKit Control Grid — read-only internal page.
 *
 * Mounts at /internal/control-grid. Pure data view: takes the Phase 1 builder
 * output and renders one row per product capability with status, confidence,
 * test counts, source, priority, next action, and (when present) failed test
 * names. No backend, no test-runner button, no writes.
 *
 * Inputs:
 *   - internal/control-grid/capabilities.json   (always present)
 *   - internal/control-grid/manual-overrides.json (optional)
 *   - tests/last-run.json                       (optional; produced by
 *                                                 `npm run test:control-grid`)
 *
 * If `tests/last-run.json` is missing, the page renders a clear empty state
 * and instructs the operator to run the script.
 */

import { useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock,
  Activity,
  RefreshCw,
  Play,
} from 'lucide-react';
import type { ControlGridRow, RowStatus } from '../utils/controlGrid/build';
import { useControlGridData } from '../utils/controlGrid/useControlGridData';

const STATUS_STYLES: Record<RowStatus, { dot: string; text: string; label: string; Icon: typeof CheckCircle2 }> = {
  healthy: { dot: 'bg-green-500', text: 'text-green-300', label: 'Healthy', Icon: CheckCircle2 },
  watch: { dot: 'bg-yellow-500', text: 'text-yellow-300', label: 'Watch', Icon: AlertTriangle },
  broken: { dot: 'bg-red-500', text: 'text-red-300', label: 'Broken', Icon: AlertCircle },
  unknown: { dot: 'bg-zinc-600', text: 'text-zinc-400', label: 'Unknown', Icon: CircleDashed },
};

function formatRelative(iso: string | null, now: number): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = now - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function ConfidenceBar({ value, status }: { value: number; status: RowStatus }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const color =
    status === 'healthy'
      ? 'bg-green-500'
      : status === 'watch'
        ? 'bg-yellow-500'
        : status === 'broken'
          ? 'bg-red-500'
          : 'bg-zinc-700';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-zinc-400 w-9 text-right">
        {pct}%
      </span>
    </div>
  );
}

function StatusPill({ status }: { status: RowStatus }) {
  const s = STATUS_STYLES[status];
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 ${s.text}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs uppercase tracking-wider">{s.label}</span>
    </span>
  );
}

function PriorityPill({ priority }: { priority: ControlGridRow['priority'] }) {
  const styles =
    priority === 'P0'
      ? 'bg-red-950/60 text-red-300 border-red-900/60'
      : priority === 'P1'
        ? 'bg-yellow-950/60 text-yellow-300 border-yellow-900/60'
        : 'bg-zinc-900 text-zinc-400 border-zinc-800';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${styles} tabular-nums`}>
      {priority}
    </span>
  );
}

function SourceBadge({ source }: { source: ControlGridRow['source'] }) {
  const map: Record<ControlGridRow['source'], string> = {
    tests: 'text-zinc-400',
    override: 'text-purple-300',
    mixed: 'text-cyan-300',
    none: 'text-zinc-600',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider ${map[source]}`}>
      {source}
    </span>
  );
}

function FailedTests({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <details className="mt-1 text-[11px]">
      <summary className="cursor-pointer text-red-400 hover:text-red-300 select-none">
        {names.length} failed test{names.length === 1 ? '' : 's'}
      </summary>
      <ul className="mt-1 ml-3 space-y-0.5 text-zinc-500">
        {names.map((n) => (
          <li key={n} className="font-mono break-all">
            {n}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function ControlGridPage() {
  const now = Date.now();
  const { result, noRun, loading, reload, running, runStatus, runTests } =
    useControlGridData();

  const counts = useMemo(() => {
    const c: Record<RowStatus, number> = { healthy: 0, watch: 0, broken: 0, unknown: 0 };
    for (const r of result.rows) c[r.status] += 1;
    return c;
  }, [result.rows]);

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100"
      style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}
    >
      {/* Header */}
      <header className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between sticky top-0 z-10 bg-zinc-950/95 backdrop-blur">
        <div className="flex items-center gap-3">
          <Activity className="w-4 h-4 text-zinc-400" />
          <h1 className="text-sm font-bold tracking-wide text-zinc-200">
            PATHKIT CONTROL GRID
          </h1>
          <span className="text-xs text-zinc-600">internal · read-only</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {counts.healthy} healthy
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
            {counts.watch} watch
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            {counts.broken} broken
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
            {counts.unknown} unknown
          </span>
          <span className="ml-3 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatRelative(result.lastRunAt, now)}
          </span>
          <button
            type="button"
            onClick={runTests}
            disabled={running}
            title="Run npm run test:control-grid via the dev server (local only)"
            className="ml-3 inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-900/60 bg-emerald-950/40 text-emerald-300 hover:text-emerald-200 hover:border-emerald-800 transition-colors disabled:opacity-50 disabled:cursor-wait"
          >
            <Play className={`w-3 h-3 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'running tests…' : 'run tests'}
          </button>
          <button
            type="button"
            onClick={reload}
            disabled={loading || running}
            title="Re-fetch tests/last-run.json without running vitest"
            className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-wait"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'loading…' : 'refresh'}
          </button>
          <a
            href="/internal/testing"
            className="ml-1 px-2 py-1 rounded border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700 transition-colors"
          >
            test harness →
          </a>
        </div>
      </header>

      {/* Run-tests outcome banner. Only shown after a UI-triggered run; the
          banner clears automatically the next time the operator runs again. */}
      {runStatus && runStatus.unavailable && (
        <div className="bg-zinc-900/80 border-b border-zinc-800 px-4 py-2 text-xs text-zinc-300 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            In-browser test runs are only available when the app is served by{' '}
            <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">
              vite dev
            </code>
            . Run{' '}
            <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">
              npm run test:control-grid
            </code>{' '}
            in a terminal instead.
          </div>
        </div>
      )}
      {runStatus && !runStatus.unavailable && (
        <div
          className={`border-b px-4 py-2 text-xs flex items-start gap-2 ${
            runStatus.ok
              ? 'bg-green-950/40 border-green-900/50 text-green-200'
              : 'bg-red-950/40 border-red-900/50 text-red-200'
          }`}
        >
          {runStatus.ok ? (
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            {runStatus.ok ? (
              <>
                Test run completed in {(runStatus.durationMs / 1000).toFixed(1)}
                s. All test-derived rows now reflect the latest results.
              </>
            ) : (
              <>
                Test run finished with failures (exit {runStatus.exitCode},{' '}
                {(runStatus.durationMs / 1000).toFixed(1)}s). The Control Grid
                rows below show the actual failing tests.
                {runStatus.stderr && (
                  <pre className="mt-1 max-h-32 overflow-auto text-[10px] text-red-300/80 whitespace-pre-wrap break-all">
                    {runStatus.stderr}
                  </pre>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* No-run banner — suppressed during initial fetch to avoid flicker. */}
      {noRun && !loading && (
        <div className="bg-zinc-900/80 border-b border-zinc-800 px-4 py-3 text-xs text-zinc-300 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-zinc-200 mb-0.5">
              No test run found
            </div>
            <div className="text-zinc-500">
              {`Run `}
              <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">
                npm run test:control-grid
              </code>
              {` to generate `}
              <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">
                tests/last-run.json
              </code>
              {`. Manual overrides still apply, and capabilities with no override are shown as `}
              <span className="text-zinc-300">unknown</span>
              {`.`}
            </div>
          </div>
        </div>
      )}

      {/* Stale banner */}
      {!noRun && result.staleRun && (
        <div className="bg-yellow-950/50 border-b border-yellow-900/50 px-4 py-3 text-xs text-yellow-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold mb-0.5">Test run is stale</div>
            <div className="text-yellow-300/80">
              Last run was {formatRelative(result.lastRunAt, now)} (older than
              24h). All test-derived rows are shown as{' '}
              <span className="text-yellow-100">unknown</span> until you re-run{' '}
              <code className="px-1 py-0.5 rounded bg-yellow-900/40 text-yellow-100">
                npm run test:control-grid
              </code>
              .
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <div className="bg-orange-950/40 border-b border-orange-900/40 px-4 py-2 text-[11px] text-orange-300">
          <span className="font-semibold">
            {result.warnings.length} mapping warning
            {result.warnings.length === 1 ? '' : 's'}:
          </span>{' '}
          {result.warnings.map((w, i) => (
            <span key={`${w.capabilityId}-${i}`} className="ml-2">
              <span className="text-orange-200">{w.capabilityId}</span>
              <span className="text-orange-500/70"> · {w.reason} · </span>
              <code className="text-orange-300">{w.mapping.file}</code>
              {w.mapping.describe && (
                <code className="text-orange-300">
                  {' › '}
                  {w.mapping.describe}
                </code>
              )}
              {i < result.warnings.length - 1 && (
                <span className="text-orange-700"> ·</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Capability
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Status
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Confidence
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Tests p / f / s / total
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Source
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Priority
              </th>
              <th className="text-left font-normal px-4 py-2 border-b border-zinc-800">
                Next action
              </th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => {
              const s = STATUS_STYLES[row.status];
              return (
                <tr
                  key={row.id}
                  className="hover:bg-zinc-900/40 transition-colors"
                >
                  <td className="px-4 py-3 border-b border-zinc-900 align-top">
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      <span className="text-zinc-200">{row.label}</span>
                    </div>
                    <div className="text-[10px] text-zinc-600 ml-3.5 mt-0.5">
                      {row.id}
                    </div>
                    {row.notes && (
                      <div className="text-[11px] text-zinc-500 ml-3.5 mt-1 max-w-md">
                        {row.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top">
                    <StatusPill status={row.status} />
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top">
                    <ConfidenceBar value={row.confidence} status={row.status} />
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top text-zinc-300 tabular-nums">
                    <div>
                      <span className="text-green-400">{row.testsPassed}</span>
                      <span className="text-zinc-700"> / </span>
                      <span
                        className={
                          row.testsFailed > 0 ? 'text-red-400' : 'text-zinc-500'
                        }
                      >
                        {row.testsFailed}
                      </span>
                      <span className="text-zinc-700"> / </span>
                      <span className="text-zinc-500">{row.testsSkipped}</span>
                      <span className="text-zinc-700"> / </span>
                      <span className="text-zinc-400">{row.testsTotal}</span>
                    </div>
                    <FailedTests names={row.failedTestNames} />
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top">
                    <SourceBadge source={row.source} />
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top">
                    <PriorityPill priority={row.priority} />
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-900 align-top text-zinc-300 max-w-sm">
                    {row.nextAction ?? (
                      <span className="text-zinc-700">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <footer className="px-4 py-3 text-[11px] text-zinc-600 border-t border-zinc-900">
        <span>
          {result.rows.length} capabilities · sourced from{' '}
          <code>internal/control-grid/capabilities.json</code>
          {` · `}
          re-run{' '}
          <code className="text-zinc-400">npm run test:control-grid</code> to
          refresh
        </span>
      </footer>
    </div>
  );
}
